import moment from 'moment';
import * as Sentry from '@sentry/browser';
import { TVisitTimeWindowCreate } from '../../../../types/TVisit';
import {
  defaultDateFormat, defaultTimeFormat, VALID_DATE_FORMATS, VALID_TIME_FORMATS, DATE_FORMAT_FAMILIES, DateFormatFamilyEnum,
} from '../../../../constants';
import { AddOrderDto } from '../../../../store/mst/models/RootStore.base';
import { DateFormatPreference } from '../../../../store/mst/models/DateFormatPreferenceEnum';
import {
  IColumnHeader, ITableCell, ITableRow,
} from '../../../../store/mst/Upload/models';
import { DateFormatFamily } from './csvDateFormatAnalytics';
import validateType from '../../../../helpers/csvUpload/isValidType';
import { CellWithDependencyMeta, CustomValidatorArgs, RowErrors } from '../types/CSVTypes';
import phoneValidator from '../validators/phoneValidator';
import emailValidator from '../validators/emailValidator';
import loadValidator from '../validators/loadValidator';
import durationValidator from '../validators/durationValidator';
import { latLngValidator } from '../validators/latLngValidator';
import { HeaderColumnKeyEnum, HeaderDataTypeEnum, HeaderNodeTypeEnum } from '../types/columnHeader';
import { handleSeparatingPackages } from './handleSeperatingPackage';
import { ADDRESS_COLUMN_KEYS } from './getColumnHeaders';
import { formatPhoneWithCountryCode } from '../../../../helpers/forms/phoneUtils';
import { addressValidator } from '../validators/addressValidator';
import customerOrderNumberValidator from '../validators/customerOrderNumberValidator';
import barcodeValidator from '../validators/barcodeValidator';
import tagsValidator from '../validators/tagsValidator';
import { sanitizeOrderObject } from './sanitizeOrderObject';

export const checkDataType = (dataCell: string | undefined): string => {
  if (!dataCell || dataCell.trim().length === 0) {
    return 'null';
  }

  if (moment(dataCell, VALID_DATE_FORMATS, true).isValid()) {
    return HeaderDataTypeEnum.DATE;
  }

  if (!Number.isNaN(Number(dataCell))) {
    return HeaderDataTypeEnum.NUMBER;
  }

  if (moment(dataCell, VALID_TIME_FORMATS, true).isValid()) {
    return HeaderDataTypeEnum.TIME;
  }

  return HeaderDataTypeEnum.STRING;
};

const convertDateFormatPreference = (preference: DateFormatPreference): DateFormatFamily | undefined => {
  if (!preference || preference === DateFormatPreference.AUTO) {
    return undefined;
  }

  switch (preference) {
    case DateFormatPreference.MMDDYYYY:
      return DateFormatFamilyEnum.MM_FIRST;
    case DateFormatPreference.DDMMYYYY:
      return DateFormatFamilyEnum.DD_FIRST;
    case DateFormatPreference.YYYYMMDD:
      return DateFormatFamilyEnum.YYYY_FIRST;
    default:
      return undefined;
  }
};

const getLocaleDefaultFormat = (): DateFormatFamily => {
  const locale = navigator.language || navigator.languages?.[0] || 'en-US';
  const isUSLocale = locale.toLowerCase().startsWith('en-us');

  return isUSLocale ? DateFormatFamilyEnum.MM_FIRST : DateFormatFamilyEnum.DD_FIRST;
};

const filterNonEmptyDateValues = (dateValues: string[]): string[] => {
  if (!dateValues || dateValues.length === 0) {
    return [];
  }
  return dateValues.filter((v) => v && v.trim().length > 0);
};

const countValidDatesForFormats = (validValues: string[], formats: string[]): number => {
  return validValues.filter((value) =>
    formats.some((format) => moment(value, format, true).isValid()),
  ).length;
};

/**
 * Determines which format family was used to parse a specific date string
 * by trying each family's formats and checking which one was valid.
 */
const detectParsedFormatFamily = (originalData: string, formats: string[]): DateFormatFamily | null => {
  if (!formats || formats.length === 0) return null;

  // Try to determine which family was used to parse this date
  if (DATE_FORMAT_FAMILIES[DateFormatFamilyEnum.MM_FIRST].some((fmt) => formats.includes(fmt) && moment(originalData, fmt, true).isValid())) {
    return DateFormatFamilyEnum.MM_FIRST;
  }
  if (DATE_FORMAT_FAMILIES[DateFormatFamilyEnum.DD_FIRST].some((fmt) => formats.includes(fmt) && moment(originalData, fmt, true).isValid())) {
    return DateFormatFamilyEnum.DD_FIRST;
  }
  if (DATE_FORMAT_FAMILIES[DateFormatFamilyEnum.YYYY_FIRST].some((fmt) => formats.includes(fmt) && moment(originalData, fmt, true).isValid())) {
    return DateFormatFamilyEnum.YYYY_FIRST;
  }
  return null;
};

export const detectBestDateFormat = (
  dateValues: string[],
  orgDateFormatPreference?: DateFormatPreference,
): DateFormatFamily => {
  // If organization has a non-AUTO preference, use that
  let orgPreferredFamily: DateFormatFamily | undefined;
  if (orgDateFormatPreference) {
    orgPreferredFamily = convertDateFormatPreference(orgDateFormatPreference);
  }
  if (orgPreferredFamily) {
    return orgPreferredFamily;
  }

  const validValues = filterNonEmptyDateValues(dateValues);

  if (validValues.length === 0) {
    return getLocaleDefaultFormat();
  }

  const mmFirstCount = countValidDatesForFormats(validValues, DATE_FORMAT_FAMILIES[DateFormatFamilyEnum.MM_FIRST]);
  const ddFirstCount = countValidDatesForFormats(validValues, DATE_FORMAT_FAMILIES[DateFormatFamilyEnum.DD_FIRST]);

  // If counts are tied, fall back to locale-based default
  if (mmFirstCount === ddFirstCount) {
    return getLocaleDefaultFormat();
  }

  return mmFirstCount > ddFirstCount ? DateFormatFamilyEnum.MM_FIRST : DateFormatFamilyEnum.DD_FIRST;
};

/**
 * Parses a date string and returns both the formatted date and the format family used.
 * Tries the preferred format family first if provided, then falls back to other formats.
 *
 * @param dateString - The date string to parse
 * @param preferredDateFormatFamily - Optional preferred format family to try first
 * @returns Object with { formattedDate, parsedFormatFamily } or { formattedDate: null, parsedFormatFamily: null } if parsing failed
 */
export const parseAndExtractDateFormatFamily = (
  dateString: string,
  preferredDateFormatFamily?: DateFormatFamily | null,
): { formattedDate: string | null; parsedFormatFamily: DateFormatFamily | null } => {
  if (!dateString || dateString.trim().length === 0) {
    return { formattedDate: null, parsedFormatFamily: null };
  }

  // Try preferred format family first if available
  if (preferredDateFormatFamily) {
    const preferredFormats = DATE_FORMAT_FAMILIES[preferredDateFormatFamily];
    const parsedDate = moment(dateString, preferredFormats, true);

    if (parsedDate.isValid()) {
      return {
        formattedDate: parsedDate.format(defaultDateFormat),
        parsedFormatFamily: preferredDateFormatFamily,
      };
    }

    // Preferred family failed, try other formats as fallback
    const otherFormats = VALID_DATE_FORMATS.filter((fmt) => !preferredFormats.includes(fmt));
    const fallbackParsedDate = moment(dateString, otherFormats, true);

    if (fallbackParsedDate.isValid()) {
      const usedFamily = detectParsedFormatFamily(dateString, otherFormats);
      return {
        formattedDate: fallbackParsedDate.format(defaultDateFormat),
        parsedFormatFamily: usedFamily,
      };
    }
  } else {
    // No preference, try all formats
    const parsedDate = moment(dateString, VALID_DATE_FORMATS, true);

    if (parsedDate.isValid()) {
      const usedFamily = detectParsedFormatFamily(dateString, VALID_DATE_FORMATS);
      return {
        formattedDate: parsedDate.format(defaultDateFormat),
        parsedFormatFamily: usedFamily,
      };
    }
  }

  return { formattedDate: null, parsedFormatFamily: null };
};

/**
 * Extracts which format family was actually used to parse a specific date string.
 * Uses the preferred format family first if provided, then falls back to other formats.
 *
 * @param dateString - The date string to analyze
 * @param preferredDateFormatFamily - Optional preferred format family to try first
 * @returns The format family that was used to parse the date, or null if parsing failed
 */
export const extractParsedDateFormatFamily = (
  dateString: string,
  preferredDateFormatFamily?: DateFormatFamily | null,
): DateFormatFamily | null => {
  const { parsedFormatFamily } = parseAndExtractDateFormatFamily(dateString, preferredDateFormatFamily);
  return parsedFormatFamily;
};

// Helper function to format time or return undefined
const _formatTimeValue = (
  originalTime: string | null | undefined,
  parsedTime: moment.Moment | undefined,
): string | undefined => {
  if (originalTime && parsedTime && parsedTime.isValid()) {
    return parsedTime.format(defaultTimeFormat);
  }
  return undefined;
};

/**
 * Converts a date string to the default date format.
 * Uses a preferred format family if available (from column analysis), otherwise tries all formats.
 *
 * Strategy:
 * 1. Try preferred format family first (e.g., MM-first: MMDDYYYY, MM-DD-YYYY, MM/DD/YYYY)
 * 2. Try YYYY-first formats next (unambiguous: YYYY-MM-DD, YYYY/MM/DD, YYYYMMDD)
 * 3. Try opposite format family last (e.g., if MM-first failed, try DD-first)
 *
 * @param originalData - The raw date string to parse
 * @param preferredDateFormatFamily - Optional format family preference (e.g., 'MM-first' or 'DD-first')
 * @returns Object with { value, data } representing the formatted date
 */
const _convertDateToType = (
  originalData: any,
  preferredDateFormatFamily?: DateFormatFamily | null,
): { value: string | null; data: string | null } => {
  const { formattedDate } = parseAndExtractDateFormatFamily(
    originalData,
    preferredDateFormatFamily,
  );

  return { value: formattedDate, data: formattedDate };
};

/**
 * Converts a number string to a number type.
 * Handles empty cells and invalid numbers by returning undefined.
 *
 * @param originalData - The raw data to convert
 * @returns Object with { value, data } representing the number or undefined
 */
const _convertNumberToType = (originalData: any): { value: number | undefined; data: number | undefined } => {
  // Excel empty cells become '' while CSV empty cells become undefined
  // Convert empty strings and whitespace-only strings to undefined so isMissingLatLng() works consistently for both formats
  if (originalData === '' || originalData?.trim() === '' || originalData == null) {
    return { data: undefined, value: undefined };
  }

  const convert = Number(originalData);
  if (!Number.isNaN(convert)) {
    return { data: convert, value: convert };
  }

  // If it's not a valid number, set to undefined for lat/lng fields to trigger geocoding
  return { data: undefined, value: undefined };
};

/**
 * Converts and sanitizes string values.
 * Trims whitespace and converts empty strings to undefined.
 *
 * @param originalData - The raw data to convert
 * @returns Object with { value, data } representing the string or undefined
 */
const _convertStringToType = (originalData: any): { value: string | undefined; data: string | undefined } => {
  // Sanitize string values: trim whitespace and convert empty strings to undefined
  if (typeof originalData === 'string') {
    const trimmed = originalData.trim();
    if (trimmed === '') {
      return { data: undefined, value: undefined };
    }
    return { data: trimmed, value: trimmed };
  }

  // Handle non-string empty values
  if (originalData === '' || originalData == null) {
    return { data: undefined, value: undefined };
  }

  return { value: originalData, data: originalData };
};

export const patchVisitTimeWindow = (visitTimeWindow: TVisitTimeWindowCreate) => {
  const timeWindowClone = { ...visitTimeWindow };
  const { startTime, endTime } = timeWindowClone;

  if (!startTime && !endTime) {
    return [];
  }

  // Parse time with strict=false to allow for AM/PM format
  const utcEndTime = endTime ? moment.utc(endTime, VALID_TIME_FORMATS, false) : undefined;
  const utcStartTime = startTime ? moment.utc(startTime, VALID_TIME_FORMATS, false) : undefined;

  // Log parsing failures to Sentry, but don't block the user experience
  if (startTime && utcStartTime && !utcStartTime.isValid()) {
    Sentry.captureMessage(
      `Failed to parse start time: "${startTime}" to UTC format`,
      { level: 'warning', extra: { startTime, formats: VALID_TIME_FORMATS } },
    );
  }

  if (endTime && utcEndTime && !utcEndTime.isValid()) {
    Sentry.captureMessage(
      `Failed to parse end time: "${endTime}" to UTC format`,
      { level: 'warning', extra: { endTime, formats: VALID_TIME_FORMATS } },
    );
  }

  // Always format the times properly, even for partial time windows
  return [{
    startTime: _formatTimeValue(startTime, utcStartTime),
    endTime: _formatTimeValue(endTime, utcEndTime),
  }];
};

export const convertDataToType = (
  dataType: string,
  originalData: any,
  preferredDateFormatFamily?: DateFormatFamily | null,
) => {
  if (dataType === HeaderDataTypeEnum.NUMBER) {
    return _convertNumberToType(originalData);
  }

  if (dataType === HeaderDataTypeEnum.DATE) {
    return _convertDateToType(originalData, preferredDateFormatFamily);
  }

  if (dataType === HeaderDataTypeEnum.STRING) {
    return _convertStringToType(originalData);
  }

  return { value: originalData, data: originalData };
};

const handleCustomFields = (visitState: AddOrderDto, columnKey: string, data: any) => {
  if (visitState.customFields) {
    return { ...visitState.customFields, [columnKey]: data };
  }
  return { [columnKey]: data };
};


const handleArrayOfObjects = (visitState: AddOrderDto, index: keyof AddOrderDto, columnKey: string, data: any) => {
  const value = [];
  if (visitState[index]) {
    if (index === 'timeWindows') {
      const firstTimeWindow = (visitState.timeWindows && visitState.timeWindows[0]) || [];
      value.push({
        ...firstTimeWindow,
        [columnKey]: data,
      });
      return value;
    }
    if (index === 'locations') {
      const firstLocation = visitState[index][0];
      value.push({
        ...firstLocation,
        [columnKey]: data,
      });
      return value;
    }
  }
  value.push({ [columnKey]: data });
  return value;
};

export const patchVisit = (visitState: AddOrderDto, originalData: any, headerSpec: IColumnHeader) => {
  let key: string = headerSpec.columnKey;
  // For date columns, use the detected format family from column analysis (if available)
  const preferredDateFormatFamily = headerSpec.dataType === HeaderDataTypeEnum.DATE
    ? headerSpec.detectedDateFormatFamily
    : null;
  let { data, value } = convertDataToType(headerSpec.dataType, originalData, preferredDateFormatFamily); // eslint-disable-line prefer-const

  // Handle barcode comma-separated values
  if (headerSpec.columnKey === HeaderColumnKeyEnum.BARCODE) {
    const existingPackages = visitState.packages || [];
    const barcodes = handleSeparatingPackages(data);

    return {
      ...visitState,
      packages: [...existingPackages, ...barcodes],
    };
  }

  // Handle ARRAY nodeType - split comma-separated values into an array
  if (headerSpec.nodeType === HeaderNodeTypeEnum.ARRAY) {
    if (data && typeof data === 'string') {
      value = data.split(',').map((item: string) => item.trim()).filter((item: string) => item.length > 0);
    } else {
      value = [];
    }
    return {
      ...visitState,
      [key]: value,
    };
  }

  if (headerSpec.nodePath !== headerSpec.columnKey) {
    const index: keyof AddOrderDto = headerSpec.nodePath;
    key = index;

    switch (headerSpec.nodeType) {
      case HeaderNodeTypeEnum.OBJECT:
        if (headerSpec.nodePath === HeaderColumnKeyEnum.CUSTOM_FIELDS) {
          value = handleCustomFields(visitState, headerSpec.columnKey, data);
        } else {
          value = { [headerSpec.columnKey]: data };
        }
        break;
      case HeaderNodeTypeEnum.ARRAY_OF_OBJECTS:
        value = handleArrayOfObjects(visitState, index, headerSpec.columnKey, data);
        break;
      default:
        value = data;
    }
  }
  return {
    ...visitState,
    [key]: value,
  };
};

const validLocationFilter = (addVisit: AddOrderDto) => {
  if (!addVisit.locations.length) return false;
  return addVisit.locations[0].address !== undefined || (addVisit.locations[0].latitude !== undefined && addVisit.locations[0].longitude);
};

export const generateNewVisits = (
  dataSource: ITableRow[],
  headers: IColumnHeader[],
  countryCode?: string,
): AddOrderDto[] => {
  const visitHeaders = headers.filter((h) => !ADDRESS_COLUMN_KEYS.includes(h.columnKey) || h.columnKey === HeaderColumnKeyEnum.ADDRESS);
  const splitAddressHeaders = headers.filter((h) => ADDRESS_COLUMN_KEYS.includes(h.columnKey) && h.columnKey !== HeaderColumnKeyEnum.ADDRESS);

  return dataSource.map((dataRow: ITableRow) => {
    const newVisit = dataRow?.cells.reduce((visit: AddOrderDto, dataCell: ITableCell) => {
      const { value, columnIdx } = dataCell;
      let cellValue: any = value;

      const foundHeaderSpec = visitHeaders
        .filter((h) => h.matched)
        .find((h) => h.matchedIndex === columnIdx);

      if (foundHeaderSpec?.columnKey === HeaderColumnKeyEnum.ADDRESS) {
        cellValue = dataRow?.locationData.address;
      }

      const matchedSplitAddressHeader = splitAddressHeaders
        .find((h) => h.matchedIndex === columnIdx);

      if (matchedSplitAddressHeader && value !== undefined) {
        if (visit.locations?.length) {
          visit.locations[0] = { ...visit.locations[0], [matchedSplitAddressHeader.columnKey]: value };
        } else {
          visit.locations = [{ [matchedSplitAddressHeader.columnKey]: value }];
        }
      }

      return foundHeaderSpec && cellValue !== undefined ? patchVisit(visit, cellValue, foundHeaderSpec) : visit;
    }, {} as AddOrderDto);

    if (newVisit.timeWindows && newVisit.timeWindows.length > 0) {
      newVisit.timeWindows = patchVisitTimeWindow(newVisit.timeWindows[0]);
    }

    if (newVisit.phone) {
      newVisit.phone = formatPhoneWithCountryCode(newVisit.phone, countryCode);
    }

    const convertDurationtoSeconds = (duration: number) => duration * 60;
    // check for duration and convert to seconds
    if (newVisit.duration != null) {
      newVisit.duration = convertDurationtoSeconds(newVisit.duration);
    }

    // Sanitize the entire order object to remove empty strings, undefined values, and empty objects/arrays
    const sanitized = sanitizeOrderObject(newVisit);
    return sanitized as AddOrderDto;
  }).filter(validLocationFilter);
};

export const generateNewCSVHeaders = (sheetHeaders: string[], columnsMapped: IColumnHeader[], currentMapping: any) => {
  const newMapping: { [id: string]: { [id: string]: number } } = {};

  sheetHeaders.forEach((val, idx) => {
    const mappedColumn = columnsMapped.find((c: IColumnHeader) => c.matchedIndex === idx);
    const newObj: { [id: string]: number } = {};

    if (mappedColumn) {
      newObj[val.toLowerCase()] = idx;
      if (Object.keys(currentMapping).includes(mappedColumn.columnKey)) {
        const oldObj: { [id: string]: number } = currentMapping[`${mappedColumn.columnKey}`];
        newMapping[`${mappedColumn.columnKey}`] = { ...oldObj, ...newObj };
      } else {
        newMapping[`${mappedColumn.columnKey}`] = newObj;
      }
    } else {
      newObj[val.toLowerCase()] = -1;
      newMapping[`${val.toLowerCase()}:${idx}`] = newObj;
    }
  });

  return { ...currentMapping, ...newMapping };
};

const customValidator = (validationData: CustomValidatorArgs) => {
  let error: string | null;
  switch (validationData.columnKey) {
    case HeaderColumnKeyEnum.ADDRESS:
      error = addressValidator(validationData);
      break;
    case HeaderColumnKeyEnum.LATITUDE:
    case HeaderColumnKeyEnum.LONGITUDE:
      error = latLngValidator(validationData);
      break;
    case HeaderColumnKeyEnum.PHONE:
      error = phoneValidator(validationData);
      break;
    case HeaderColumnKeyEnum.EMAIL:
      error = emailValidator(validationData);
      break;
    case HeaderColumnKeyEnum.LOAD:
      error = loadValidator(validationData);
      break;
    case HeaderColumnKeyEnum.DURATION:
      error = durationValidator(validationData);
      break;
    case HeaderColumnKeyEnum.START_TIME:
    case HeaderColumnKeyEnum.END_TIME:
      error = null;
      break;
    case HeaderColumnKeyEnum.CUSTOMER_ORDER_NUMBER:
      error = customerOrderNumberValidator(validationData);
      break;
    case HeaderColumnKeyEnum.DELIVERY_DATE:
      error = null;
      break;
    case HeaderColumnKeyEnum.BARCODE:
      error = barcodeValidator(validationData);
      break;
    case HeaderColumnKeyEnum.TAGS:
      error = tagsValidator(validationData);
      break;
    default:
      error = null;
  }
  return error;
};

export const getErrors = (cells: CellWithDependencyMeta[], header: any, countryCode?: string, tableData?: any, locationHeaders?: any): RowErrors =>
  cells.reduce((acc: RowErrors, data: CellWithDependencyMeta) => {
    const { cell } = data;
    const isValidType = validateType(cell.type, header.dataType, header.required);

    if (!isValidType) {
      acc[cell.rowIdx] = 'dataType';
    } else {
      const hasErrors = customValidator?.({
        value: cell.value,
        columnKey: header.columnKey,
        ...(header.dependsOn ? { dependsOn: data.dependsOn?.value } : {}),
        ...(header.columnKey === HeaderColumnKeyEnum.PHONE ? { countryCode } : {}),
        type: cell.type,
        row: tableData[cell.rowIdx],
        locationHeaders,
      });

      if (hasErrors) {
        acc[cell.rowIdx] = hasErrors;
      }
    }

    return acc;
  }, {});


export const removeEmptyRows = (content: string[][]) => {
  // filters out rows that are either empty or contain only empty/whitespace-only cells
  return content.filter((row: string[]) => row.length && !!row.find(((cell: string | undefined) => cell && cell.trim().length > 0)));
};

